/**
 * Authentication hook wrapping MSAL for login, logout, and token acquisition.
 */

import { useMsal, useIsAuthenticated, useAccount } from "@azure/msal-react";
import { useCallback } from "react";
import { loginRequest, apiScopes } from "@/lib/msal-config";

interface AuthState {
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
  readonly userId: string | null;
  readonly displayName: string | null;
  readonly email: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
}

/**
 * Provides authentication state and actions using MSAL React hooks.
 */
export function useAuth(): AuthState {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0]);

  const login = useCallback(async (): Promise<void> => {
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(async (): Promise<void> => {
    await instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (!account) {
      throw new Error("No authenticated account available");
    }

    const response = await instance.acquireTokenSilent({
      scopes: [...apiScopes],
      account,
    });

    return response.accessToken;
  }, [instance, account]);

  return {
    isAuthenticated,
    isLoading: inProgress !== "none",
    userId: account?.localAccountId ?? null,
    displayName: account?.name ?? null,
    email: account?.username ?? null,
    login,
    logout,
    getAccessToken,
  };
}
